import React, { useState, useEffect } from 'react';
import { useManagerAuth } from '../../context/ManagerAuthContext';
import { Link } from 'react-router-dom';
import { 
  Package, AlertTriangle, XCircle, Warehouse, ShoppingCart, 
  TrendingUp, Clock, ArrowRight, CheckCircle2, Activity, 
  Boxes, ShieldCheck, Zap, RefreshCw, Layers, ArrowUpRight, Search
} from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4002';

const ManagerDashboard = () => {
  const { getToken, manager } = useManagerAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchDashboardData = async () => {
    try {
      setRefreshing(true);
      const res = await fetch(`${API}/api/manager/dashboard`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const json = await res.json();
      if (json.success) {
        setData(json);
      }
    } catch (err) {
      console.error('❌ Error fetching manager dashboard:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="py-24 flex flex-col items-center justify-center space-y-4">
        <div className="relative">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 animate-spin flex items-center justify-center p-1 shadow-lg shadow-orange-500/20">
            <div className="w-full h-full bg-slate-900 rounded-xl" />
          </div>
          <Activity className="w-6 h-6 text-orange-500 absolute inset-0 m-auto animate-pulse" />
        </div>
        <p className="text-xs font-black uppercase tracking-widest text-slate-400">
          Loading Manager Radar...
        </p>
      </div>
    );
  }

  const s = data?.summary || {};
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good Morning' : hour < 17 ? 'Good Afternoon' : 'Good Evening';
  const managerFirstName = manager?.full_name?.split(' ')[0] || 'Manager';

  return (
    <div className="space-y-8 select-none font-sans pb-12">
      
      {/* ── 1. HERO EXECUTIVE WELCOME HEADER ── */}
      <div className="relative bg-gradient-to-r from-slate-900 via-slate-800 to-orange-950 rounded-3xl p-6 sm:p-8 text-white border border-slate-700/80 shadow-2xl overflow-hidden group">
        
        {/* Soft Radial Ambient Glow */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-orange-500/20 to-amber-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-10 -left-10 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2">
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight uppercase italic text-white flex items-center gap-3">
              {greeting}, {managerFirstName}! 👋
            </h1>
          </div>

          {/* Quick Hub Actions */}
          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={fetchDashboardData}
              disabled={refreshing}
              className="p-3 bg-slate-800/80 hover:bg-slate-700 border border-slate-600 rounded-2xl text-slate-300 hover:text-white transition-all shadow-md active:scale-95 disabled:opacity-50"
              title="Refresh Dashboard Telemetry"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin text-orange-400' : ''}`} />
            </button>

            <Link
              to="/manager/inventory"
              className="px-5 py-3 bg-gradient-to-r from-orange-600 to-amber-500 hover:from-orange-500 hover:to-amber-400 text-white text-xs font-black uppercase tracking-wider rounded-2xl flex items-center gap-2 shadow-lg shadow-orange-600/30 transition-all hover:scale-105 active:scale-95"
            >
              <Boxes className="w-4 h-4" />
              <span>Manage Inventory</span>
            </Link>
          </div>
        </div>
      </div>

      {/* ── 2. METRIC KPI TELEMETRY CARDS ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        
        {/* KPI 1: Pending Orders */}
        <Link
          to="/manager/orders"
          className="bg-white rounded-3xl p-5 border border-slate-200 hover:border-blue-400 transition-all duration-300 shadow-sm hover:shadow-xl group relative overflow-hidden flex flex-col justify-between"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-2xl bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600 group-hover:scale-110 transition-transform">
              <ShoppingCart className="w-6 h-6" />
            </div>
            <span className="text-[10px] font-black uppercase tracking-widest text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full border border-blue-100">
              DISPATCH
            </span>
          </div>

          <div>
            <p className="text-3xl font-black text-slate-900 font-mono tracking-tight">
              {s.pending_orders ?? 0}
            </p>
            <p className="text-xs font-black uppercase tracking-wider text-slate-700 mt-1">
              Pending Orders
            </p>
            <p className="text-[11px] text-slate-500 font-medium mt-0.5">Awaiting fulfillment</p>
          </div>

          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px] font-bold text-blue-600">
            <span>View Pending Queue</span>
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </div>
        </Link>

        {/* KPI 2: Total Stock Units */}
        <Link
          to="/manager/inventory"
          className="bg-white rounded-3xl p-5 border border-slate-200 hover:border-emerald-400 transition-all duration-300 shadow-sm hover:shadow-xl group relative overflow-hidden flex flex-col justify-between"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600 group-hover:scale-110 transition-transform">
              <Package className="w-6 h-6" />
            </div>
            <span className="text-[10px] font-black uppercase tracking-widest text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-100">
              RESERVES
            </span>
          </div>

          <div>
            <p className="text-3xl font-black text-slate-900 font-mono tracking-tight">
              {(s.total_quantity || 0).toLocaleString()}
            </p>
            <p className="text-xs font-black uppercase tracking-wider text-slate-700 mt-1">
              Total Stock Units
            </p>
            <p className="text-[11px] text-slate-500 font-medium mt-0.5">Across assigned hubs</p>
          </div>

          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px] font-bold text-emerald-600">
            <span>Audit Full Stock</span>
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </div>
        </Link>

        {/* KPI 3: Low Stock Warning */}
        <Link
          to="/manager/inventory?filter=low"
          className="bg-white rounded-3xl p-5 border border-slate-200 hover:border-amber-400 transition-all duration-300 shadow-sm hover:shadow-xl group relative overflow-hidden flex flex-col justify-between"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 group-hover:scale-110 transition-transform">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <span className="text-[10px] font-black uppercase tracking-widest text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-200">
              LOW STOCK
            </span>
          </div>

          <div>
            <p className="text-3xl font-black text-slate-900 font-mono tracking-tight">
              {s.low_stock_alerts ?? 0}
            </p>
            <p className="text-xs font-black uppercase tracking-wider text-slate-700 mt-1">
              Low Stock Alerts
            </p>
            <p className="text-[11px] text-slate-500 font-medium mt-0.5">Requires replenishment</p>
          </div>

          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px] font-bold text-amber-700">
            <span>Restock Items</span>
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </div>
        </Link>

        {/* KPI 4: Out of Stock */}
        <Link
          to="/manager/inventory?filter=out"
          className="bg-white rounded-3xl p-5 border border-slate-200 hover:border-rose-400 transition-all duration-300 shadow-sm hover:shadow-xl group relative overflow-hidden flex flex-col justify-between"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-2xl bg-rose-50 border border-rose-200 flex items-center justify-center text-rose-600 group-hover:scale-110 transition-transform">
              <XCircle className="w-6 h-6" />
            </div>
            <span className="text-[10px] font-black uppercase tracking-widest text-rose-600 bg-rose-50 px-2.5 py-1 rounded-full border border-rose-200">
              CRITICAL
            </span>
          </div>

          <div>
            <p className="text-3xl font-black text-slate-900 font-mono tracking-tight">
              {s.out_of_stock ?? 0}
            </p>
            <p className="text-xs font-black uppercase tracking-wider text-slate-700 mt-1">
              Out of Stock
            </p>
            <p className="text-[11px] text-slate-500 font-medium mt-0.5">Zero available units</p>
          </div>

          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px] font-bold text-rose-600">
            <span>Resolve Stockout</span>
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </div>
        </Link>

      </div>

      {/* ── 3. WAREHOUSE NODES TELEMETRY MATRIX ── */}
      <div className="space-y-4">
        <div className="flex items-center justify-between pb-2 border-b border-slate-200">
          <h2 className="text-lg font-black text-slate-900 uppercase tracking-tight flex items-center gap-2">
            <Warehouse className="w-5 h-5 text-orange-600" />
            <span>ASSIGNED WAREHOUSE NODES</span>
          </h2>
          <span className="text-xs font-black text-slate-400 uppercase tracking-widest">
            {(data?.warehouses || []).length} NODE(S) ACTIVE
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {(data?.warehouses || []).map(w => {
            const availQty = Math.max(0, (w.total_qty || 0) - (w.reserved_qty || 0));
            const totalQty = Math.max(1, w.total_qty || 1);
            const availPct = Math.min(100, Math.round((availQty / totalQty) * 100));

            return (
              <div 
                key={w.id} 
                className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm hover:shadow-xl transition-all duration-300 space-y-5 relative overflow-hidden"
              >
                {/* Node Header */}
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white shadow-md shadow-orange-500/20">
                      <Warehouse className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-base font-black text-slate-900 uppercase tracking-tight">
                        {w.name}
                      </h3>
                      <p className="text-xs font-bold text-slate-500">
                        {w.code} • {w.city}, {w.state}
                      </p>
                    </div>
                  </div>

                  <span className={`text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full border ${
                    w.is_active 
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                      : 'bg-slate-100 text-slate-600 border-slate-200'
                  }`}>
                    {w.is_active ? '● OPERATIONAL' : 'INACTIVE'}
                  </span>
                </div>

                {/* Node Quick Stats Grid */}
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-3">
                    <p className="text-lg font-black text-slate-900 font-mono">{w.total_skus || 0}</p>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">TOTAL SKUs</p>
                  </div>
                  <div className="bg-emerald-50/80 border border-emerald-200 rounded-2xl p-3">
                    <p className="text-lg font-black text-emerald-700 font-mono">{availQty.toLocaleString()}</p>
                    <p className="text-[10px] font-black text-emerald-600 uppercase tracking-wider">AVAILABLE</p>
                  </div>
                  <div className={`border rounded-2xl p-3 ${
                    w.low_stock > 0 
                      ? 'bg-amber-50/80 border-amber-200' 
                      : 'bg-slate-50 border-slate-200/80'
                  }`}>
                    <p className={`text-lg font-black font-mono ${
                      w.low_stock > 0 ? 'text-amber-700' : 'text-slate-700'
                    }`}>
                      {w.low_stock || 0}
                    </p>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">LOW STOCK</p>
                  </div>
                </div>

                {/* Stock Reserves Progress Bar */}
                <div className="space-y-1.5 pt-1">
                  <div className="flex items-center justify-between text-[11px] font-bold">
                    <span className="text-slate-500 uppercase tracking-wider">Stock Availability Ratio</span>
                    <span className="text-slate-900 font-mono">{availPct}% Available ({availQty.toLocaleString()} / {(w.total_qty || 0).toLocaleString()})</span>
                  </div>
                  <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all duration-700 ${
                        availPct > 50 ? 'bg-gradient-to-r from-emerald-400 to-green-500' :
                        availPct > 20 ? 'bg-gradient-to-r from-amber-400 to-orange-500' :
                                       'bg-gradient-to-r from-rose-500 to-red-600'
                      }`}
                      style={{ width: `${availPct}%` }}
                    />
                  </div>
                </div>

                {/* Out of Stock Notice */}
                {w.out_of_stock > 0 && (
                  <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3.5 py-2 flex items-center gap-2 font-bold">
                    <XCircle className="w-4 h-4 text-rose-600 shrink-0" />
                    <span>{w.out_of_stock} product SKU(s) currently out of stock at this hub</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── 4. RECENT ORDERS STREAM & REALTIME ACTIVITY FEED ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pt-2">
        
        {/* Recent Dispatch Orders */}
        <div className="space-y-4">
          <div className="flex items-center justify-between pb-2 border-b border-slate-200">
            <h2 className="text-base font-black text-slate-900 uppercase tracking-tight flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-blue-600" />
              <span>RECENT DISPATCH QUEUE</span>
            </h2>
            <Link 
              to="/manager/orders" 
              className="text-xs font-black text-orange-600 hover:text-orange-700 uppercase tracking-wider flex items-center gap-1 group"
            >
              <span>View All Orders</span>
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm divide-y divide-slate-100">
            {(data?.recent_orders || []).length === 0 ? (
              <div className="py-12 flex flex-col items-center gap-2 text-slate-400">
                <ShoppingCart className="w-10 h-10 text-slate-300" />
                <p className="text-xs font-black uppercase tracking-widest">No recent order activity</p>
              </div>
            ) : (
              (data?.recent_orders || []).slice(0, 6).map(o => (
                <Link 
                  key={o.id} 
                  to="/manager/orders"
                  className="flex items-center justify-between p-4 hover:bg-orange-50/40 transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-700 font-mono font-black text-xs group-hover:border-orange-300 transition-colors">
                      #{o.order_number ? String(o.order_number).slice(-4) : o.id.slice(0, 4)}
                    </div>
                    <div>
                      <p className="text-xs font-black text-slate-900 uppercase">
                        {o.order_number || o.id.slice(0, 8)}
                      </p>
                      <p className="text-[11px] font-medium text-slate-500">
                        {o.customer?.full_name || 'Customer Order'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className={`text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full border ${
                      o.status === 'confirmed'  ? 'bg-blue-50 text-blue-700 border-blue-200' :
                      o.status === 'processing' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                      o.status === 'shipped'    ? 'bg-purple-50 text-purple-700 border-purple-200' :
                                                  'bg-emerald-50 text-emerald-700 border-emerald-200'
                    }`}>
                      {o.status}
                    </span>
                    <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-orange-600 group-hover:translate-x-1 transition-all" />
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>

        {/* Real-time Stock Activity Radar */}
        <div className="space-y-4">
          <div className="flex items-center justify-between pb-2 border-b border-slate-200">
            <h2 className="text-base font-black text-slate-900 uppercase tracking-tight flex items-center gap-2">
              <Activity className="w-5 h-5 text-emerald-600" />
              <span>LIVE STOCK MOVEMENT RADAR</span>
            </h2>
            <Link 
              to="/manager/inventory" 
              className="text-xs font-black text-orange-600 hover:text-orange-700 uppercase tracking-wider flex items-center gap-1 group"
            >
              <span>View Audit Log</span>
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm divide-y divide-slate-100">
            {(data?.recent_transactions || []).length === 0 ? (
              <div className="py-12 flex flex-col items-center gap-2 text-slate-400">
                <Activity className="w-10 h-10 text-slate-300" />
                <p className="text-xs font-black uppercase tracking-widest">No stock activity logged</p>
              </div>
            ) : (
              (data?.recent_transactions || []).slice(0, 6).map(t => (
                <div key={t.id} className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xs font-black font-mono border ${
                      t.transaction_type === 'STOCK_IN' 
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                        : 'bg-rose-50 text-rose-700 border-rose-200'
                    }`}>
                      {t.transaction_type === 'STOCK_IN' ? '+' : '-'}{t.quantity}
                    </div>
                    <div>
                      <p className="text-xs font-black text-slate-900 uppercase line-clamp-1">
                        {t.product?.name || 'Stock Adjustment'}
                      </p>
                      <p className="text-[10px] font-medium text-slate-400 font-mono">
                        {new Date(t.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>

                  <span className={`text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-md border ${
                    t.transaction_type === 'STOCK_IN'
                      ? 'bg-emerald-100/70 text-emerald-800 border-emerald-300'
                      : 'bg-rose-100/70 text-rose-800 border-rose-300'
                  }`}>
                    {t.transaction_type}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

      </div>

    </div>
  );
};

export default ManagerDashboard;

